<template>
  <base-print
    title="SALES INVOICE"
    :document-number="'No. ' + invoice.invoice_code"
    :document-date="invoice.invoice_date"
    document-label=""
    print-id="invoicePrint"
  >
    <!-- Customer Information -->
    <div class="info-section" v-if="invoice.customer">
      <div class="info-row">
        <span class="info-label">Customer Name:</span>
        {{ invoice.customer.customer_name }}
      </div>
      <div class="info-row">
        <span class="info-label">Address:</span>
        Purok {{ invoice.customer.customer_purok }}, Brgy.
        {{ invoice.customer.customer_barangay }},
        {{ invoice.customer.customer_municipality }}, Surigao del Sur
      </div>
      <div class="info-row">
        <span class="info-label">Contact Number:</span>
        {{ invoice.customer.customer_contact_no }}
      </div>
    </div>

    <div class="info-row" v-if="invoice.purpose">
      <span class="info-label">Remarks/Purpose:</span>
      {{ invoice.purpose }}
    </div>

    <div class="divider-thin"></div>

    <!-- Invoice Items Table -->
    <h3 style="font-size: 12pt; margin: 10px 0; font-weight: bold">
      Invoice Items
    </h3>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 40px">No.</th>
          <th>Description</th>
          <th v-if="displayPrice" style="width: 100px" class="text-right">
            Price
          </th>
          <th style="width: 80px" class="text-center">Qty</th>
          <th v-if="displayPrice" style="width: 120px" class="text-right">
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in filteredItems" :key="item.id">
          <td class="text-center">{{ index + 1 }}</td>
          <td>
            {{ getItemName(item) }}
            <div
              v-if="item.item_serials && item.item_serials.length"
              style="font-size: 8pt; color: #555"
            >
              S/N:
              {{ item.item_serials.map((s) => s.serial_number).join(", ") }}
            </div>
          </td>
          <td v-if="displayPrice" class="text-right">
            {{ formatNumber(getUnitPrice(item)) }}
          </td>
          <td class="text-center">{{ item.quantity }}</td>
          <td v-if="displayPrice" class="text-right">
            {{ formatNumber(item.amount) }}
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Summary Section -->
    <div v-if="displayPrice" class="clearfix">
      <div class="summary-section">
        <div class="summary-row">
          <span>Subtotal:</span>
          <span>{{ formatNumber(invoice.amount) }}</span>
        </div>
        <div class="summary-row" v-if="invoice.discount > 0">
          <span>Discount:</span>
          <span>({{ formatNumber(invoice.discount) }})</span>
        </div>
        <div class="summary-row total">
          <span>Invoice Total:</span>
          <span>{{ formatNumber(invoice.total_amount) }}</span>
        </div>
      </div>
    </div>

    <!-- Payments Table -->
    <div
      v-if="invoice.payments && invoice.payments.length > 0"
      style="clear: both; margin-top: 80px"
    >
      <h3 style="font-size: 12pt; margin: 10px 0; font-weight: bold">
        Payments
      </h3>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 80px">Payment ID</th>
            <th>Payment Method</th>
            <th style="width: 120px">Payment Date</th>
            <th style="width: 120px" class="text-right">Amount Paid</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="payment in invoice.payments" :key="payment.id">
            <td>{{ payment.id }}</td>
            <td>
              {{
                payment.chart_of_account
                  ? payment.chart_of_account.account_title
                  : ""
              }}
            </td>
            <td>{{ formatDate(payment.payment_date) }}</td>
            <td class="text-right">{{ formatNumber(payment.amount_paid) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Balance and Status -->
    <div
      v-if="displayPrice"
      style="margin-top: 20px; border-top: 2px solid #000; padding-top: 10px"
    >
      <div class="info-row">
        <span class="info-label">Balance:</span>
        <strong>{{ formatNumber(invoice.balance) }}</strong>
      </div>
      <div class="info-row">
        <span class="info-label">Amount Paid:</span>
        <strong>{{
          formatNumber(invoice.total_amount - invoice.balance)
        }}</strong>
      </div>
      <div class="info-row">
        <span class="info-label">Invoice Status:</span>
        <strong>{{ invoice.invoice_status }}</strong>
      </div>
    </div>
  </base-print>
</template>

<script>
import BasePrint from "./BasePrint.vue";
import moment from "moment";

export default {
  name: "PrintInvoice",
  components: {
    BasePrint,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    displayPrice: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    filteredItems() {
      // Filter out tax fee items (id 650), include both item and service types
      return this.invoice.invoice_items.filter((item) => {
        if (item.item && item.item.id === 650) return false; // skip tax fee
        return item.item || item.service; // include items and services
      });
    },
  },
  methods: {
    getItemName(invoiceItem) {
      if (invoiceItem.item) return invoiceItem.item.item_name;
      if (invoiceItem.service)
        return invoiceItem.service.service_name + " (Service)";
      return "";
    },
    getUnitPrice(invoiceItem) {
      if (invoiceItem.item) return invoiceItem.item.selling_price;
      if (invoiceItem.service) return invoiceItem.service.service_amount;
      return 0;
    },
    formatNumber(value) {
      return new Intl.NumberFormat("en-PH").format(value || 0);
    },
    formatDate(date) {
      return moment(date).format("MMMM DD, YYYY");
    },
  },
};
</script>
